import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';

import * as Sentry from '@sentry/react';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes
} from 'react-router-dom';

import './styles/variables.css';
import './styles/components.css';
import './styles/layout.css';

import { ArtifactShareHost } from './components/ArtifactShareModal';
import { EventTrackerProvider } from './components/EventTrackerProvider';
import { PageNavigationScrollReset } from './components/PageNavigationScrollReset';
import {
  hasInitialProjectBootstrap,
  RouteLoadingFallback,
  shouldSkipPlanningSkeleton,
} from './components/PlanningRouteShell';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SignupCouponCapture } from './components/SignupCouponCapture';
import { SignupCreditAwardBridge } from './components/SignupCreditAwardBridge';
import { SupportNotificationBridge } from './components/Support/SupportNotificationBridge';
import { ToastProvider } from './components/Toast';
import { AuthProvider } from './context/AuthContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { usePageTracking } from './hooks/usePageTracking';
import Landing from './pages/landing';
import { store } from './store';
import { captureMagSrvToken } from './utils/analytics';
import { captureVisit } from './utils/visitorTracker';

Sentry.init({
  // Development already has the React error overlay and should never emit
  // production telemetry. This also keeps localhost network traces focused
  // on product requests.
  enabled: process.env.NODE_ENV === 'production',
  dsn: "https://37802e61ef4bfc6c4a616c4b8daaec7d@o4511466654728192.ingest.us.sentry.io/4511471718170624",
  environment: process.env.NODE_ENV,
  tracesSampleRate: 0.1,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Errors are useful in production; user-identifying defaults are not needed.
  sendDefaultPii: false
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const PlanningPrebootCleanup: React.FC = () => {
  React.useLayoutEffect(() => {
    document.documentElement.classList.remove('planning-route-preboot');
  }, []);

  return null;
};

captureMagSrvToken();
const trackingHostname = window.location.hostname.toLowerCase();
const trackingPathname = window.location.pathname;
const isProductionTrackingHost = trackingHostname === '8080.ai' || trackingHostname === 'www.8080.ai';
const isPlanningPath = trackingPathname === '/planning' || trackingPathname.startsWith('/planning/');
const isAuthCallbackPath = trackingPathname.startsWith('/auth/callback/')
  || trackingPathname === '/github/repo-auth/callback';

if (isProductionTrackingHost && !isPlanningPath && !isAuthCallbackPath) captureVisit();

// -- Lazy-loaded pages (route-based code splitting) --
const AboutPage = React.lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const PricingPage = React.lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PlanningPage = React.lazy(() => import('./pages/PlanningPage').then(m => ({ default: m.PlanningPage })));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const BillingPage = React.lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const ChatPage = React.lazy(() => import('./pages/chat/ChatPage'));
const ModelsPage = React.lazy(() => import('./pages/ModelsPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage').then(m => ({ default: m.TeamPage })));
const TeamListPage = React.lazy(() => import('./pages/TeamListPage').then(m => ({ default: m.TeamListPage })));
const TeamAnalyticsPage = React.lazy(() => import('./pages/TeamAnalyticsPage').then(m => ({ default: m.TeamAnalyticsPage })));
const GrowthFeedbackPage = React.lazy(() => import('./pages/GrowthFeedbackPage').then(m => ({ default: m.GrowthFeedbackPage })));
const OrganizationManagementPage = React.lazy(() => import('./pages/OrganizationManagementPage').then(m => ({ default: m.OrganizationManagementPage })));
const OrganizationInvitationAcceptPage = React.lazy(() => import('./pages/OrganizationInvitationAcceptPage').then(m => ({ default: m.OrganizationInvitationAcceptPage })));
const InvitationAcceptPage = React.lazy(() => import('./pages/InvitationAcceptPage').then(m => ({ default: m.InvitationAcceptPage })));
const AdminSettingsPage = React.lazy(() => import('./pages/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const AdminProjectsPage = React.lazy(() => import('./pages/admin/AdminProjectsPage').then(m => ({ default: m.AdminProjectsPage })));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminMonthlyInvoicesPage = React.lazy(() => import('./pages/admin/AdminMonthlyInvoicesPage').then(m => ({ default: m.AdminMonthlyInvoicesPage })));
const AdminCouponsPage = React.lazy(() => import('./pages/admin/AdminCouponsPage').then(m => ({ default: m.AdminCouponsPage })));
const AdminCashoutsPage = React.lazy(() => import('./pages/admin/AdminCashoutsPage').then(m => ({ default: m.AdminCashoutsPage })));
const IntentAnalyticsPage = React.lazy(() => import('./pages/admin/IntentAnalyticsPage').then(m => ({ default: m.IntentAnalyticsPage })));
const QualityAnalyticsPage = React.lazy(() => import('./pages/admin/QualityAnalyticsPage').then(m => ({ default: m.QualityAnalyticsPage })));
const PerformanceAnalyticsPage = React.lazy(() => import('./pages/admin/PerformanceAnalyticsPage').then(m => ({ default: m.PerformanceAnalyticsPage })));
const AgentManagerPage = React.lazy(() => import('./pages/admin/AgentManagerPage').then(m => ({ default: m.AgentManagerPage })));
const TrafficAnalysisPage = React.lazy(() => import('./pages/admin/TrafficAnalysisPage').then(m => ({ default: m.TrafficAnalysisPage })));
const AdminModelsPage = React.lazy(() => import('./pages/admin/AdminModelsPage').then(m => ({ default: m.AdminModelsPage })));
const ModelLogsPage = React.lazy(() => import('./pages/admin/ModelLogsPage').then(m => ({ default: m.ModelLogsPage })));
const GenerationLogsPage = React.lazy(() => import('./pages/admin/GenerationLogsPage').then(m => ({ default: m.GenerationLogsPage })));
const ModelAnalyticsPage = React.lazy(() => import('./pages/admin/ModelAnalyticsPage').then(m => ({ default: m.ModelAnalyticsPage })));
const CallbackGoogle = React.lazy(() => import('./pages/auth/CallbackGoogle').then(m => ({ default: m.CallbackGoogle })));
const CallbackApple = React.lazy(() => import('./pages/auth/CallbackApple').then(m => ({ default: m.CallbackApple })));
const CallbackMicrosoft = React.lazy(() => import('./pages/auth/CallbackMicrosoft').then(m => ({ default: m.CallbackMicrosoft })));
const CallbackGitHub = React.lazy(() => import('./pages/auth/CallbackGitHub').then(m => ({ default: m.CallbackGitHub })));
const CallbackGitHubRepoConnect = React.lazy(() => import('./pages/auth/CallbackGitHubRepoConnect').then(m => ({ default: m.CallbackGitHubRepoConnect })));
const VscodeAuthPage = React.lazy(() => import('./pages/auth/VscodeAuthPage').then(m => ({ default: m.VscodeAuthPage })));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SharedDesignPage = React.lazy(() => import('./pages/SharedDesignPage'));
const SharedArtifactPage = React.lazy(() => import('./pages/SharedArtifactPage'));
const PublicProjectPage = React.lazy(() => import('./pages/PublicProjectPage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const SkillStorePage = React.lazy(() => import('./pages/SkillStorePage'));
const SkillRunnerPage = React.lazy(() => import('./pages/SkillRunnerPage'));
const ReferralCapture = React.lazy(() => import('./components/ReferralCapture').then(m => ({ default: m.ReferralCapture })));
const ClaimSharePage = React.lazy(() => import('./pages/ClaimSharePage').then(m => ({ default: m.ClaimSharePage })));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const SupportDashboardPage = React.lazy(() => import('./pages/SupportDashboardPage'));
const APIPlatformPage = React.lazy(() => import('./pages/APIPlatformPage'));
const ProofPage = React.lazy(() => import('./pages/ProofPage'));

const PageTracker: React.FC = () => { usePageTracking();

  return null; };

const AppRouteLoadingFallback: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  return (
    <RouteLoadingFallback
      pathname={location.pathname}
      skipPlanningSkeleton={shouldSkipPlanningSkeleton(
        navigationType,
        hasInitialProjectBootstrap(location.state),
      )}
    />
  );
};

const rootEl = document.getElementById('root') as HTMLElement;



const app = (
  <React.StrictMode>
    <Sentry.ErrorBoundary>
      <Provider store={store}>
        <Router>
        <PlanningPrebootCleanup />
        <PageTracker />
        <SignupCouponCapture />
        <ToastProvider>
          <AuthProvider>
            <EventTrackerProvider />
            <SignupCreditAwardBridge />
            <SupportNotificationBridge />
            <AuthModalProvider>
              <ArtifactShareHost />
              <Suspense fallback={<AppRouteLoadingFallback />}>
                <SentryRoutes>
                  <Route path="/" element={<Landing />} />
                    {/* real auth URLs: prerendered for no-JS; with JS the Navbar opens the modal in place */}
                    <Route path="/login" element={<Landing />} />
                    <Route path="/signup" element={<Landing />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/models" element={<ModelsPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/proof" element={<><PageNavigationScrollReset /><ProofPage /></>} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/auth/callback/google" element={<CallbackGoogle />} />
                  <Route path="/auth/callback/apple" element={<CallbackApple />} />
                  <Route path="/auth/callback/microsoft" element={<CallbackMicrosoft />} />
                  <Route path="/auth/callback/github" element={<CallbackGitHub />} />
                  <Route path="/github/repo-auth/callback" element={<CallbackGitHubRepoConnect />} />
                  <Route path="/vscode-auth" element={<VscodeAuthPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/refer/:referralCode" element={<ReferralCapture />} />
                    <Route path="/claim-share/:token" element={<ClaimSharePage />} />
                  <Route
                    path="/chat/:conversationId?"
                    element={
                      <ProtectedRoute>
                        <ChatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planning"
                    element={
                      <ProtectedRoute>
                        <PlanningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planning/:projectId/*"
                    element={
                      <ProtectedRoute>
                        <PlanningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:projectId/team"
                    element={
                      <ProtectedRoute>
                        <TeamPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/invite/:token" element={<InvitationAcceptPage />} />
                  <Route path="/organization-invite/:token" element={<OrganizationInvitationAcceptPage />} />
                  <Route
                    path="/team"
                    element={
                      <ProtectedRoute>
                        <TeamListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team/analytics"
                    element={
                      <ProtectedRoute>
                        <TeamAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/growth-feedback"
                    element={
                      <ProtectedRoute>
                        <GrowthFeedbackPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizations"
                    element={
                      <ProtectedRoute>
                        <OrganizationManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute>
                        <AdminSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/projects"
                    element={
                      <ProtectedRoute>
                        <AdminProjectsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute>
                        <AdminUsersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/coupons"
                    element={
                      <ProtectedRoute>
                        <AdminCouponsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/monthly-invoices"
                    element={
                      <ProtectedRoute>
                        <AdminMonthlyInvoicesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/cashouts"
                    element={
                      <ProtectedRoute>
                        <AdminCashoutsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/traffic-analysis"
                    element={
                      <ProtectedRoute>
                        <TrafficAnalysisPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/intent-analytics"
                    element={
                      <ProtectedRoute>
                        <IntentAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/agent-manager"
                    element={
                      <ProtectedRoute>
                        <AgentManagerPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/quality-analytics"
                    element={
                      <ProtectedRoute>
                        <QualityAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/performance-analytics"
                    element={
                      <ProtectedRoute>
                        <PerformanceAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/models"
                    element={
                      <ProtectedRoute>
                        <AdminModelsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/model-logs"
                    element={
                      <ProtectedRoute>
                        <ModelLogsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/model-analytics"
                    element={
                      <ProtectedRoute>
                        <ModelAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/page-generation-logs"
                    element={<ProtectedRoute><GenerationLogsPage key="page" kind="page" /></ProtectedRoute>}
                  />
                  <Route
                    path="/admin/section-generation-logs"
                    element={<ProtectedRoute><GenerationLogsPage key="section" kind="section" /></ProtectedRoute>}
                  />
                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute>
                        <BillingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/api-platform"
                    element={
                      <ProtectedRoute>
                        <APIPlatformPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <ProjectsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/design/:projectId/:shareId" element={<SharedDesignPage />} />
                  <Route path="/shared/artifacts/:shareId" element={<SharedArtifactPage />} />
                  <Route path="/projects/:projectId/*" element={<PublicProjectPage />} />
                  <Route path="/explore" element={<ExplorePage />} />
                  <Route path="/skill-store" element={<SkillStorePage />} />
                  <Route path="/skill-store/:skillId" element={<SkillRunnerPage />} />
                  <Route
                    path="/support"
                    element={
                      <ProtectedRoute>
                        <SupportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/support/tickets/:ticketId"
                    element={
                      <ProtectedRoute>
                        <SupportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/support/dashboard"
                    element={
                      <ProtectedRoute>
                        <SupportDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </SentryRoutes>
              </Suspense>
            </AuthModalProvider>
          </AuthProvider>
        </ToastProvider>
      </Router>
      </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// The planning hard-reload shell is authored in public/index.html so it can
// paint before the JavaScript bundle. It is not a react-snap snapshot; the
// first React commit replaces it with the same geometry-aware component.
const hasPlanningPrebootShell = rootEl.firstElementChild?.id === 'planning-route-preboot';
// Only an element counts as a snapshot: outside /planning the inline body
// script removes the preboot shell and leaves whitespace text nodes behind,
// which must not trigger hydration (it would mismatch and re-render anyway).
const hasSnapshot = rootEl.firstElementChild !== null && !hasPlanningPrebootShell;

if (hasSnapshot) {
  ReactDOM.hydrateRoot(rootEl, app, {
    onRecoverableError: (error: any) => {
      // Suppress expected hydration mismatch warnings between stale snapshots and live data
      const msg = error?.message || '';
      if (
        msg.includes('Hydration failed') ||
        msg.includes('There was an error while hydrating') ||
        msg.includes('Text content did not match') ||
        msg.includes('did not match')
      ) {
        return;
      }
      console.error(error);
    }
  });
} else {
  ReactDOM.createRoot(rootEl).render(app);
}
